import React from 'react';
import styles from './styles.module.scss';
import { Button, Flex, Modal, Typography } from 'shared/_ui';
import { CONTROL_IDS } from 'shared/consts';
import { daysUntilDate, useStoreUiControls } from 'shared/lib';
import { useTranslation } from 'react-i18next';
import { useFetchUserInfo } from 'entities/Connection';

const PremiumExpirationWarningNineDays: React.FC = () => {
  const { t } = useTranslation();

  const [isMounted, setIsMounted] = React.useState<boolean>(false);
  const shownStorageKeyRef = React.useRef<string | null>(null);

  const { isOpen, close } = useStoreUiControls(
    CONTROL_IDS.PREMIUM_EXPIRATION_BANNER_9
  );

  const markAsShown = React.useCallback(() => {
    if (shownStorageKeyRef.current) {
      window.localStorage.setItem(shownStorageKeyRef.current, 'true');
    }
  }, []);

  const onClick = () => {
    close();
    markAsShown();
  };

  const { data: userInfo } = useFetchUserInfo(null);
  React.useEffect(() => {
    if (!userInfo || !userInfo.paidTo) {
      setIsMounted(false);
      return;
    }

    const daysLeft = daysUntilDate(userInfo.paidTo);
    const isIndiaPricing = userInfo.pricingRegion === 'IN';
    const initialReminderDays = isIndiaPricing ? 3 : 9;
    const nextReminderDays = isIndiaPricing ? 2 : 3;
    const shouldShow = daysLeft <= initialReminderDays && daysLeft > nextReminderDays;
    const storageKey = [
      'premium-expiration-initial',
      userInfo.pricingRegion ?? 'DEFAULT',
      userInfo.paidTo,
    ].join(':');
    const wasShown = window.localStorage.getItem(storageKey) === 'true';
    const shownInCurrentRender = shownStorageKeyRef.current === storageKey;
    const shouldMount =
      shouldShow && (isOpen === null || isOpen) && (!wasShown || shownInCurrentRender);

    if (shouldMount && !wasShown) {
      shownStorageKeyRef.current = storageKey;
      window.localStorage.setItem(storageKey, 'true');
    }

    setIsMounted(shouldMount);
  }, [isOpen, userInfo])


  if (!isMounted) return null;

  const trialPeriodDays = userInfo?.trialPeriodDays ?? 14;

  return (
    <Modal isOpen={true} onClose={()=>{close();markAsShown()}}>
      <Flex
        className={styles.container}
        direction="column"
        spacing={32}
        align="center"
      >
        <Flex direction="column" spacing={24}>
          <Typography.Title level={1}>
            {t('banner.premiumExpiration_9.title', { count: trialPeriodDays })}
          </Typography.Title>
          <Flex direction="column" spacing={8}>
            <Typography.Text size={3}>
              {t('banner.premiumExpiration_9.subtitle', { count: trialPeriodDays })}
            </Typography.Text>
            {t('banner.premiumExpiration_9.description') && (
              <Typography.Text type="secondary" size={3}>
                {t('banner.premiumExpiration_9.description')}
              </Typography.Text>
            )}
          </Flex>
        </Flex>
        <Button className={styles.button} onClick={onClick} type="primary">
          {t('banner.premiumExpiration_9.button')}
        </Button>
      </Flex>
    </Modal>
  );
};

export { PremiumExpirationWarningNineDays };
